import React, {ReactNode, useEffect, useRef} from "react";
import styles from '../mna-info.module.scss';
import classnames from 'classnames/bind';
import {ErrorMessage, Field, FormikValues, getIn, useFormikContext} from "formik";
import {InputBox} from "../edit-common";
import {useInjection} from "inversify-react";
import {DialogManager} from "../../../components/dialog-manager";
import { CustomerDetail } from "../../../model/customer/dto/customer-detail";
import { CompanyPfKw, EarningsEstimate } from "../../../model/customer/dto/customer-mna-brokerage-dto";
import { PrFrStatCont } from "../../../model/customer/dto/financial-statement";
import FormikErrorMsg from "../../../components/formik-error-msg";
import { CustomerMnaNeedsDto } from "../../../model/customer/dto/customer-mna-needs-dto";
import {isShowTempSaveBtn, needsBtnTitle} from "../mna-info";

const cx = classnames.bind(styles);

interface CompanyTypeFormProp {
    data: CustomerDetail
    needs: CustomerMnaNeedsDto
    info: CompanyPfKw[]
    perform: EarningsEstimate[]
    setIsDraft: React.Dispatch<React.SetStateAction<boolean>>
}

interface TextAreaBoxProps {
    name: string;
    title?: ReactNode;
    hasNoti?: string;
    isInnerTitle?: boolean;
    initialH?: string;
    required?: boolean;
    textAreaCn?: string;
}

export const TextAreaBox = ({name, title, hasNoti, isInnerTitle, initialH = '7.2rem', required, textAreaCn}: TextAreaBoxProps) => {
    const visibleRef = useRef<HTMLTextAreaElement|null>(null);

    const adjustHeight = () => {
        if (visibleRef.current) {
            visibleRef.current.style.height = 'auto';
            visibleRef.current.style.height = `${visibleRef.current.scrollHeight}px`; // 내용에 맞게 높이 조정
        }
    };

    useEffect(() => {
        if (visibleRef.current) {
            visibleRef.current.style.height = `${visibleRef.current.scrollHeight}px`;
        }
    }, []);

    return (
        <div className={cx('text-area-box')}>
            {title && <label className={cx(isInnerTitle ? 'inner-title' : '')}>{title}<i className={cx('noti-num')}> {hasNoti && hasNoti}</i></label>}
            <Field className={textAreaCn} innerRef={visibleRef} onInput={adjustHeight} name={name} as='textarea' placeholder='입력' style={{minHeight: initialH}} required={required}/>
            {name && <FormikErrorMsg name={name}/>}
        </div>
    );
}

const CompanyTypeForm = ({data, needs, info, perform, setIsDraft} : CompanyTypeFormProp) => {
    const dialogManager = useInjection(DialogManager);
    const {values, getFieldProps, setFieldValue, touched, errors, submitCount, validateForm, resetForm} = useFormikContext<FormikValues>();

    useEffect(() => {
        resetForm({
            values: { ...values },
            errors: {},
            touched: {},
            submitCount: 0,
        });
    }, [values.companyType]);

    let sales: number | undefined;
    let profit;
    let profitRate;
    let growth;

    const fss = data.fss && data.fss.length > 0 ? data.fss : undefined;
    const lastYear = new Date().getFullYear();
    const findIdxList: number[] = [];

    useEffect(() => {
        perform.forEach((v, i) => {
            let salesGrowth = 0;
            let currentSales = (getFieldProps(`perform.${i}.sales`).value ?? 0) * 1e5;
            let currentProfit = (getFieldProps(`perform.${i}.profit`).value ?? 0) * 1e5;

            if (i === 0) {
                if (sales && sales > 0) {
                    salesGrowth = currentSales / sales - 1;
                }
            } else {
                let prevSales = perform[i - 1].sales * 1e5;

                if (currentSales && currentSales > 0) {
                    salesGrowth = prevSales === 0 ? 0 : currentSales / prevSales - 1;
                }
            }

            let profitRate = currentSales === 0 ? 0 : currentProfit / currentSales;

            setFieldValue(`perform.${i}.salesGrowthRate`, (salesGrowth * 100).toFixed(2)).then();
            setFieldValue(`perform.${i}.profitRate`, (profitRate * 100).toFixed(2)).then();
        });
    }, [values]);

    useEffect(() => {
        if(submitCount > 0){
            (async () => {
                const errors = await validateForm();
                if (Object.keys(errors).length > 0) {
                    dialogManager.alert("M&A니즈 정보 필수값\n미입력 정보를 확인해 주세요.");
                }
            })();
        }
    }, [validateForm, submitCount]);

    perform.some((p, idx) => {
        if (p.year < lastYear) {
            findIdxList.push(idx);
        }
    });

    if(findIdxList.length > 0)
    {
        findIdxList.some((fi) => {
            perform.splice(fi, 1);
        });

        const performLastYear = perform[perform.length - 1].year;

        for(let i = 0; i < 5 - perform.length; i++)
        {
            perform.push(
                {
                    year: performLastYear + (i + 1),
                    sales: 0,
                    profit: 0
                }
            )
        }
    }

    if (fss) {
        for (let i = 0; i < fss[0].iss.length; i++) {
            const iss: PrFrStatCont = fss[0].iss[i];
            const stDate = Number(iss.stDate.substring(0, 4));
            const fsYear = fss[0].refDt;

            // if(lastYear === stDate) {
            if(fsYear === stDate) {
                sales = Number(iss.data["sales"] ?? 0);
                // const opIncome = Number(iss.data["opIncome"] ?? 0);
                profit = Number(iss.data["opIncome"] ?? 0);
                profitRate = sales === 0 ? 0 : (profit / sales) * 100;

                if (fss[0].iss.length > 1) {
                    if(i !== fss[0].iss.length - 1) {
                        const prevIss = fss[0].iss[i + 1];
                        const prevSales = Number(prevIss.data["sales"] ?? 0);
                        growth = prevSales === 0 ? 0 : (sales / prevSales) - 1;
                    }
                }

                break;
            }
        }
    }

    return (
        <div className={cx('company-type-form')}>
            <TextAreaBox
                title='1. 매각기업 주요 제품'
                name={'brokerage.mainProduct'}
                textAreaCn={cx((getIn(touched, 'brokerage.mainProduct') || submitCount > 0) && getIn(errors, 'brokerage.mainProduct') ? 'error' : '')}/>
            {/*<p className={cx('sub-title')}>2. 사업영역</p>*/}
            {/*<InputBox*/}
            {/*    name={'businessName'}*/}
            {/*    inputWidth='100%'*/}
            {/*    readOnly*/}
            {/*    placeholder='입력'*/}
            {/*    inputboxCn={cx('select-box', (touched.businessName || submitCount > 0) && errors.businessName ? 'error' : '')}*/}
            {/*          onClick={() => {*/}
            {/*              dialogManager.open(BusinessFieldPop, {*/}
            {/*                  info: info,*/}
            {/*                  name: "brokerage",*/}
            {/*                  setFieldValue: setFieldValue*/}
            {/*              });*/}
            {/*          }}/>*/}
            <TextAreaBox
                title='3. 사업분야 상세 설명'
                name='brokerage.bizCateDetail'
                textAreaCn={cx((getIn(touched, 'brokerage.bizCateDetail') || submitCount > 0) && getIn(errors, 'brokerage.bizCateDetail') ? 'error' : '')}
                initialH='15rem'/>
            {/* <p className={cx('sub-title', 'gap2')}>4. {lastYear}년 매출액 및 영업이익 예상치, 향후 5년간 실적 전망</p> */}
            <p className={cx('sub-title', 'gap2')}>4. 매출액 및 영업이익 예상치, 향후 5년간 실적 전망  {(getIn(touched, `perform`) || submitCount > 0) && getIn(errors, `perform`) ? <ErrorMessage name='perform' render={msg => <i className={cx('error-message')}> {msg}</i>}/> : null }</p>
            <div className={cx('flex-row-box')}>
                <div className={cx('flex-column-box')}>
                    <p className={cx('opacity-0')}>제목</p>
                    <p className={cx('no-input-box')}>매출액</p>
                    <p className={cx('no-input-box')}>영업이익</p>
                    <p className={cx('no-input-box')}>매출액증가율</p>
                    <p className={cx('no-input-box')}>영업이익률</p>
                </div>
                <div className={cx('flex-column-box')}>
                    {/* <p className={cx('opacity-0')}>제목</p> */}
                    <p>{fss && fss[0].refDt ? fss[0].refDt : new Date().getFullYear()}년<i
                        className={cx('noti-num')}> 1)</i></p>
                    {
                        fss ?
                            <>
                                <p className={cx('no-input-box')}>{sales ? (sales / 1e5).toFixed(2) + "억원" : "-"}</p>
                                <p className={cx('no-input-box')}>{profit ? (profit / 1e5).toFixed(2) + "억원" : "-"}</p>
                                <p className={cx('no-input-box')}>{growth ? (growth * 100).toFixed(1) + "%" : "-"}</p>
                                <p className={cx('no-input-box')}>{profitRate ? (profitRate).toFixed(1) + "%" : "-"}</p>
                            </>
                            :
                            <>
                                <p className={cx('no-input-box')}>-</p>
                                <p className={cx('no-input-box')}>-</p>
                                <p className={cx('no-input-box')}>-</p>
                                <p className={cx('no-input-box')}>-</p>
                            </>
                    }

                </div>
                {
                    perform.map((data, i) => {
                        return (
                            <div key={data.year} className={cx('flex-column-box')}>
                                <p>{data.year}년</p>
                                <InputBox
                                    inputboxCn={cx('input-box', (getIn(touched, `perform.${i}.sales`) || submitCount > 0) && getIn(errors, `perform.${i}.sales`) ? 'error' : '')}
                                    name={`perform.${i}.sales`}
                                    type='number' unit='억원' inputWidth='10.4rem'/>
                                <InputBox
                                    inputboxCn={cx('input-box', (getIn(touched, `perform.${i}.profit`) || submitCount > 0) && getIn(errors, `perform.${i}.profit`) ? 'error' : '')}
                                    name={`perform.${i}.profit`}
                                    type='number' unit='억원' inputWidth='10.4rem'/>
                                <InputBox
                                    inputboxCn={cx('input-box')}
                                    name={`perform.${i}.salesGrowthRate`}
                                    type='number' unit='%'
                                    inputWidth='10.4rem' readOnly={true}/>
                                <InputBox
                                    inputboxCn={cx('input-box')}
                                    name={`perform.${i}.profitRate`}
                                    type='number' unit='%'
                                    inputWidth='10.4rem' readOnly={true}/>
                            </div>
                        )
                    })
                }
            </div>
            <p className={cx('noti')}>주1) 상세재무정보 메뉴에서 2024년 재무제표 정보를 꼭 반영해 주세요.</p>
            <TextAreaBox name='brokerage.earningsEstimatesNote'/>
            <TextAreaBox
                title='5. 해당 기업의 주요 매출처 및 매출규모'
                name='brokerage.mainSalesChannel'
                textAreaCn={cx((getIn(touched, 'brokerage.mainSalesChannel') || submitCount > 0) && getIn(errors, 'brokerage.mainSalesChannel') ? 'error' : '')}
                initialH='15rem'/>
            <TextAreaBox
                title={<p className={cx('plus_info')}>6. 사업용부동산 및 생산라인<span>(*해당사항 있을 경우)</span></p>}
                name='brokerage.estateAndProdLine'
                initialH='15rem'/>
            <TextAreaBox
                title='7. 주요 경쟁자'
                name='brokerage.mainCompetitors'
                textAreaCn={cx((getIn(touched, 'brokerage.mainCompetitors') || submitCount > 0) && getIn(errors, 'brokerage.mainCompetitors') ? 'error' : '')}
                initialH='15rem'/>
            <TextAreaBox
                title='8. 해당 사업 아이템의 경쟁우위 요소'
                name='brokerage.uniqueSellingPoints'
                textAreaCn={cx((getIn(touched, 'brokerage.uniqueSellingPoints') || submitCount > 0) && getIn(errors, 'brokerage.uniqueSellingPoints') ? 'error' : '')}
                initialH='15rem'/>
            <TextAreaBox
                title={<p className={cx('plus_info')}>9. 기술사항 / 연구 및 개발 중인 사항<span>(*해당사항 있을 경우)</span></p>}
                name='brokerage.technicalDetails'
                initialH='15rem'/>
            <TextAreaBox
                title='10. 해당 기업을 인수했을 때 시너지를 낼 수 있는 기업 (사업분야)'
                name='brokerage.synergisticPartners'
                textAreaCn={cx((getIn(touched, 'brokerage.synergisticPartners') || submitCount > 0) && getIn(errors, 'brokerage.synergisticPartners') ? 'error' : '')}
                initialH='15rem'/>
            <TextAreaBox
                title='11. 기업매각사유'
                name='brokerage.salesReason'
                textAreaCn={cx((getIn(touched, 'brokerage.salesReason') || submitCount > 0) && getIn(errors, 'brokerage.salesReason') ? 'error' : '')}/>
            <TextAreaBox
                title={<p className={cx('plus_info')}>12. 희망하는 매각방식 <span>(*특정 조건 있을 경우 한에)</span></p>}
                name='brokerage.salesMethod'/>
            <TextAreaBox
                title='13. 매각희망금액 (산정 근거)'
                name='brokerage.salesAmount'
                textAreaCn={cx((getIn(touched, 'brokerage.salesAmount') || submitCount > 0) && getIn(errors, 'brokerage.salesAmount') ? 'error' : '')}/>
            <TextAreaBox
                title='14. 과거 기업인수 제안 받은 경험'
                name='brokerage.experRecvAcqOffer'
                textAreaCn={cx((getIn(touched, 'brokerage.experRecvAcqOffer') || submitCount > 0) && getIn(errors, 'brokerage.experRecvAcqOffer') ? 'error' : '')}/>
            <TextAreaBox
                title='15. 주주 구성 (대주주, 임직원, 투자자 지분율)'
                hasNoti='2)'
                name='brokerage.shareHolderComposition'
                textAreaCn={cx((getIn(touched, 'brokerage.shareHolderComposition') || submitCount > 0) && getIn(errors, 'brokerage.shareHolderComposition') ? 'error' : '')}/>
            <p className={cx('noti')}>주2) 주주현황 메뉴에서 정확한 보유 지분, 경영자와의 관계를 반영해 주세요.</p>
            <TextAreaBox
                title='16. 대표이사 이외 회사 경영 실무를 이끌어 갈 수 있는 핵심 임원 존재 여부'
                name='brokerage.keyExecutives'
                textAreaCn={cx((getIn(touched, 'brokerage.keyExecutives') || submitCount > 0) && getIn(errors, 'brokerage.keyExecutives') ? 'error' : '')}/>
            <TextAreaBox
                title='17. 매각이 어려울 경우 생각해 둔 B플랜 여부'
                name='brokerage.planB'
                textAreaCn={cx((getIn(touched, 'brokerage.planB') || submitCount > 0) && getIn(errors, 'brokerage.planB') ? 'error' : '')}/>
            <p className={cx('noti2')}>※ 회사 소개자료, 사업계획서, 최근 3개년 세무조정계산서가 있는 경우, 상담일지에 업로드 해주세요.</p>
            <div className={cx('btn-area')}>
                {isShowTempSaveBtn(needs, values.companyType) && <button className={cx('temporary-save-btn')} type='submit' onClick={() => setIsDraft(true)}>임시 저장하기</button>}
                <button className={cx('save-btn')} type='submit'  onClick={() => setIsDraft(false)}><i></i>{`${needsBtnTitle(needs, values.companyType)}`}
                </button>
            </div>
        </div>
    );
}

export default CompanyTypeForm;